import React, { Fragment, useEffect, useState } from 'react';
import { Container } from '@/components/container';
import { OfferContent } from './';
import { Navbar, NavbarActions } from '@/partials/navbar';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { KeenIcon } from '@/components';
import { PageMenu } from './menu/PageMenu';
import { OfferDetailsData } from '@/pages/hettwer/types/OfferDetailsData';
import { Loading } from '@/layouts/hettwer/loading';
import { enqueueSnackbar } from 'notistack';
import { OfferProfileHero } from '@/pages/hettwer/offer/hero/OfferHero';
import { DeleteOfferConfirmationModal } from '@/pages/hettwer/offer/blocks/delete-offer/DeleteOfferModal.tsx';
import { usePrivacyMode } from '@/utils/privacyMode';

const OfferPage = () => {
  const { p_id } = useParams<{ p_id: string }>();
  const navigate = useNavigate();
  const { isPrivacyMode, togglePrivacyMode } = usePrivacyMode();

  const [offer, setOffer] = useState<OfferDetailsData | null>(null);
  const [loading, setLoading] = useState(true);
  const { tab } = useParams<{ tab?: string }>();
  const [activePage, setActivePage] = useState<string>(tab || 'overview');

  const [refreshKey, setRefreshKey] = useState(0);
  const triggerRefresh = () => setRefreshKey((prev) => prev + 1);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const API_OFFER = `${import.meta.env.VITE_APP_API_URL}/offer`;

  const fetchOfferData = async () => {
    setLoading(true);
    try {
      const response = await axios.get<OfferDetailsData>(`${API_OFFER}/${p_id}`, {
        headers: { Accept: 'application/json' }
      });
      setOffer(response.data);
    } catch {
      enqueueSnackbar(`Angebot konnte nicht geladen werden`, {
        variant: 'solid',
        state: 'danger'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (p_id) {
      fetchOfferData();
    }
  }, [p_id, refreshKey]);

  const handleSaveOffer = async (updatedOffer: OfferDetailsData) => {
    try {
      await axios.put(`${API_OFFER}/${p_id}`, updatedOffer, {
        headers: { 'Content-Type': 'application/json' }
      });

      enqueueSnackbar('Angebot erfolgreich gespeichert', {
        variant: 'solid',
        state: 'success'
      });

      triggerRefresh();
    } catch {
      enqueueSnackbar('Fehler beim Speichern des Angebots', {
        variant: 'solid',
        state: 'danger'
      });
    }
  };

  const handleDeleteOffer = async () => {
    try {
      await axios.delete(`${API_OFFER}/${p_id}`);

      enqueueSnackbar('Angebot wurde gelöscht', {
        variant: 'solid',
        state: 'success'
      });

      navigate('/offers');
    } catch {
      enqueueSnackbar('Fehler beim Löschen des Angebots', {
        variant: 'solid',
        state: 'danger'
      });
    }
  };

  if (loading) return <Loading loading={loading} />;
  if (!offer) return <div>Kein Angebotsdatensatz verfügbar</div>;

  return (
    <Fragment>
      <OfferProfileHero offer={offer} />

      <Container>
        <Navbar>
          <PageMenu setActivePage={setActivePage} />
          <NavbarActions>
            <button
              onClick={togglePrivacyMode}
              type="button"
              aria-pressed={isPrivacyMode}
              className={`btn btn-sm ml-2 ${isPrivacyMode ? 'btn-warning' : 'btn-light'}`}
            >
              <KeenIcon icon={isPrivacyMode ? 'eye-slash' : 'eye'} />
              {isPrivacyMode ? 'Personendaten verborgen' : 'Personendaten sichtbar'}
            </button>

            <button
              onClick={() => handleSaveOffer(offer)}
              type="button"
              className="btn btn-sm btn-success ml-2"
            >
              <KeenIcon icon="check" />
              Angebot speichern
            </button>

            <button
              onClick={() => setDeleteModalOpen(true)}
              type="button"
              className="btn btn-sm btn-danger ml-2"
            >
              <KeenIcon icon="trash" />
              Angebot löschen
            </button>
          </NavbarActions>
        </Navbar>
      </Container>

      <Container>
        <OfferContent
          offer={offer}
          activePage={activePage}
          loading={loading}
          setActivePage={setActivePage}
          onOfferChange={setOffer}
        />
      </Container>

      <DeleteOfferConfirmationModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteOffer}
        offerNumber={offer.offer_number ?? ''}
      />
    </Fragment>
  );
};

export { OfferPage };
